import { Link } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import { AppShell, Section, EmptyState } from '../../../components/layout';
import { Card, Button, SkillMeter } from '../../../components/ui';
import { cn } from '../../../lib/cn';

export default function DashboardIndex({ student, current_session, courses, skills, vocab_nudge }) {
  const { t } = useTranslation();
  const { active = [], completed = [] } = courses || {};

  return (
    <AppShell student={student} scrollTitle={t('nav.dashboard')}>
      <div className="max-w-5xl mx-auto">
        <h1 data-testid="student-dashboard-heading" className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
          {t('studentDashboard.welcomeBack')} {student.display_name || student.first_name}
        </h1>

        {/* Vocabulary — front and center: spans every course's words */}
        <VocabularyHero nudge={vocab_nudge} />

        {/* Continue Session */}
        {current_session && (
          <Section className="mb-10">
            <Card gradient elevated className="relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-gray-100 dark:from-gray-700 to-transparent rounded-bl-full opacity-50" />
              <div className="relative">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  {t('studentDashboard.continueWhereLeftOff')}
                </p>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
                  {current_session.activity_title}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {current_session.activity_subtitle}
                </p>
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex-1 max-w-xs">
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gray-900 dark:bg-white rounded-full transition-all duration-500"
                        style={{ width: `${current_session.progress_percent}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {current_session.progress_percent}% {t('common.complete')}
                  </span>
                </div>
                <Link href={`/student/practice/${current_session.id}`}>
                  <Button variant="primary" size="md">
                    {t('common.continue')}
                    <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Button>
                </Link>
              </div>
            </Card>
          </Section>
        )}

        {/* Active Courses */}
        {active.length > 0 && (
          <Section title={t('studentDashboard.yourCourses')}>
            <div className="space-y-4">
              {active.map((course) => (
                <CourseCard key={course.id} course={course} />
              ))}
            </div>
          </Section>
        )}

        {/* Completed Courses */}
        {completed.length > 0 && (
          <Section title={t('common.completed')}>
            <div className="space-y-4">
              {completed.map((course) => (
                <CourseCard key={course.id} course={course} completed />
              ))}
            </div>
          </Section>
        )}

        {/* Empty State */}
        {active.length === 0 && completed.length === 0 && (
          <Section>
            <EmptyState
              title={t('studentDashboard.noCourses')}
              description={t('studentDashboard.noCoursesHelp')}
            />
          </Section>
        )}

        {/* Skills */}
        <SkillsSection skills={skills} />
      </div>
    </AppShell>
  );
}

function CourseCard({ course, completed = false }) {
  const { t } = useTranslation();
  return (
    <Card className="hover:shadow-md transition-shadow">
      <div className="flex items-start gap-5">
        <BooksPreview books={course.books} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-1">
            <Link
              href={course.course_url}
              className="text-lg font-semibold text-gray-900 dark:text-white hover:underline"
            >
              {course.title}
            </Link>
            {completed && (
              <span className="text-xs font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-2 py-0.5 rounded-full flex-shrink-0 ml-3">
                {t('common.completed')}
              </span>
            )}
          </div>

          {course.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
              {course.description}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400 mb-2">
            {course.date_range && (
              <span className="flex items-center gap-1">
                <CalendarIcon className="w-3.5 h-3.5" />
                {course.date_range}
              </span>
            )}
            <span>{course.unit_count} {course.unit_count === 1 ? t('common.unit') : t('common.units')}</span>
            <span>{course.lesson_count} {course.lesson_count === 1 ? t('common.lesson') : t('common.lessons')}</span>
          </div>

          {course.lesson_count > 0 && (
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-400 dark:bg-indigo-500 rounded-full transition-all duration-500"
                  style={{ width: `${Math.round((course.completed_lesson_count / course.lesson_count) * 100)}%` }}
                />
              </div>
              <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap flex-shrink-0">
                {t('studentDashboard.lessonsComplete', { completed: course.completed_lesson_count, total: course.lesson_count })}
              </span>
            </div>
          )}

          {/* Books */}
          {course.books.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {course.books.map((book) => (
                <span
                  key={book.id}
                  className="inline-flex items-center gap-1 text-xs text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded"
                >
                  <BookIcon className="w-3 h-3" />
                  {book.title}
                  {book.author && <span className="text-gray-400 dark:text-gray-500">— {book.author}</span>}
                </span>
              ))}
            </div>
          )}

          <Link href={completed ? course.course_url : (course.next_practice_url || course.course_url)}>
            <Button variant="secondary" size="sm">
              {completed ? t('studentDashboard.review') : t('studentDashboard.continueLearning')}
              <svg className="ml-1.5 w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Button>
          </Link>
        </div>
      </div>
    </Card>
  );
}

function BooksPreview({ books }) {
  if (!books || books.length === 0) {
    return (
      <div className="w-16 h-22 flex-shrink-0 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded flex items-center justify-center">
        <BookIcon className="w-6 h-6 text-gray-300 dark:text-gray-600" />
      </div>
    );
  }

  const displayBooks = books.slice(0, 3);

  return (
    <div className="flex flex-shrink-0">
      {displayBooks.map((book, index) => (
        <div
          key={book.id}
          className="relative w-14 h-20"
          style={{
            zIndex: displayBooks.length - index,
            marginLeft: index > 0 ? '-8px' : '0',
          }}
        >
          {book.cover_url ? (
            <img
              src={book.cover_url}
              alt={book.title}
              className="w-full h-full object-cover rounded shadow-sm"
              title={book.title}
            />
          ) : (
            <div
              className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-600 dark:to-gray-700 rounded shadow-sm flex items-center justify-center"
              title={book.title}
            >
              <BookIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            </div>
          )}
        </div>
      ))}
      {books.length > 3 && (
        <div
          className="w-14 h-20 bg-gray-100 dark:bg-gray-700 rounded shadow-sm flex items-center justify-center"
          style={{ marginLeft: '-8px', zIndex: 0 }}
        >
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">+{books.length - 3}</span>
        </div>
      )}
    </div>
  );
}

function SkillsSection({ skills }) {
  const { t } = useTranslation();
  if (!skills || skills.length === 0) {
    return (
      <Section title={t('studentDashboard.yourSkills')}>
        <Card>
          <div className="text-center py-6">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('studentDashboard.skillsHelp')}
            </p>
          </div>
        </Card>
      </Section>
    );
  }

  return (
    <Section title={t('studentDashboard.yourSkills')}>
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {skills.slice(0, 6).map((skill) => (
            <SkillMeter
              key={skill.skill_id}
              name={skill.name}
              value={skill.mastery_estimate}
              max={100}
            />
          ))}
        </div>
        {skills.length > 6 && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-4 text-center">
            +{skills.length - 6} {t('studentDashboard.moreSkills')}
          </p>
        )}
      </Card>
    </Section>
  );
}

// Vocabulary hero — front-and-center on the dashboard. Vocabulary practice
// pulls words from across every course, so it gets its own prominent surface
// rather than living inside a single course card.
function VocabularyHero({ nudge }) {
  const { t } = useTranslation();
  const reviewDue = nudge?.review_due ?? 0;
  const newCount = nudge?.new_count ?? 0;
  const learning = nudge?.learning_count ?? 0;
  const mastered = nudge?.mastered_count ?? 0;
  const total = nudge?.total_count ?? 0;

  if (total === 0) return null; // no vocabulary yet — nothing to show

  let headline;
  if (reviewDue > 0) headline = t('studentDashboard.vocabReviewDue', { count: reviewDue });
  else if (newCount > 0) headline = t('studentDashboard.vocabOnboardingPending', { count: newCount });
  else headline = t('studentDashboard.vocabAllCaughtUp');

  const ctaLabel = reviewDue > 0 || newCount > 0
    ? t('studentDashboard.vocabPracticeNow')
    : t('studentDashboard.studyVocabulary');

  const pct = (n) => (total > 0 ? Math.max(n > 0 ? 4 : 0, (n / total) * 100) : 0);

  return (
    <Section className="mb-10">
      <Card gradient elevated className="relative overflow-hidden">
        <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-indigo-100/70 dark:from-indigo-500/10 to-transparent rounded-bl-full opacity-60" />
        <div className="relative">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            {t('studentDashboard.vocabulary')}
          </p>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">
            {headline}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-5">
            {t('studentDashboard.vocabAcrossCourses', { count: total })}
          </p>

          {/* Mastery journey bar: mastered -> learning -> new */}
          <div className="flex h-2.5 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700 mb-3">
            <div className="bg-emerald-400 dark:bg-emerald-500 transition-all" style={{ width: `${pct(mastered)}%` }} />
            <div className="bg-indigo-400 dark:bg-indigo-500 transition-all" style={{ width: `${pct(learning)}%` }} />
            <div className="bg-gray-300 dark:bg-gray-500 transition-all" style={{ width: `${pct(newCount)}%` }} />
          </div>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mb-6">
            <VocabLegend dot="bg-emerald-400 dark:bg-emerald-500" count={mastered} label={t('studentDashboard.vocabMastered')} />
            <VocabLegend dot="bg-indigo-400 dark:bg-indigo-500" count={learning} label={t('studentDashboard.vocabLearning')} />
            <VocabLegend dot="bg-gray-300 dark:bg-gray-500" count={newCount} label={t('studentDashboard.vocabNew')} />
          </div>

          <Link href="/student/dictionary">
            <Button variant="primary" size="md">
              {ctaLabel}
              <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Button>
          </Link>
        </div>
      </Card>
    </Section>
  );
}

function VocabLegend({ dot, count, label }) {
  return (
    <span className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
      <span className={cn('w-2.5 h-2.5 rounded-full', dot)} />
      <span className="font-semibold text-gray-900 dark:text-white">{count}</span>
      {label}
    </span>
  );
}

function BookIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  );
}

function CalendarIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}
